import * as DialogPrimitive from '@radix-ui/react-dialog'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading01Icon } from '@hugeicons/core-free-icons'
import { Dialog, DialogPortal } from '@/components/ui/dialog'
import { formatDataPointAllowance, formatShortDate, getFeatureAccessForPlan, getPlanById } from '@/lib/constants/pricing'
import { cn } from '@/lib/utils'
import { ChipGlyph } from './UrgencyBanner'

export interface TrialExpiredStats {
  sessions: number | null
  funnels: number | null
  heatmaps: number | null
}

/** "500K data points · 5 funnels, heatmaps and A/B tests", read from pricing.ts. */
export function buildPlanSummary(planId: 'business' | 'scale'): string {
  const plan = getPlanById(planId)
  const f = getFeatureAccessForPlan(planId)
  const points = `${formatDataPointAllowance(Number(plan?.eventLimit ?? 0))} data points`
  if (f.funnelLimit === f.heatmapLimit && f.heatmapLimit === f.activeSplitTestLimit) {
    const n = Number.isFinite(f.funnelLimit) ? `${f.funnelLimit}` : 'Unlimited'
    return `${points} · ${n} funnels, heatmaps and A/B tests`
  }
  return points
}

function count(n: number, one: string, many: string) {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`
}

/** What the expired trial kept. Only counts the account really has; never a deletion date. */
export function buildSavedList(stats: TrialExpiredStats): string[] {
  const items: string[] = []
  if (stats.sessions) items.push(count(stats.sessions, 'recorded session', 'recorded sessions'))
  const built: string[] = []
  if (stats.funnels) built.push(count(stats.funnels, 'funnel', 'funnels'))
  if (stats.heatmaps) built.push(count(stats.heatmaps, 'heatmap', 'heatmaps'))
  if (built.length) items.push(built.join(' and '))
  items.push('Pages, sources and Stripe revenue')
  return items
}

export interface TrialExpiredPaywallProps {
  open: boolean
  planName: string
  monthlyPrice: number
  planSummary: string
  /** Site named in the headline; omit when the account tracks several. */
  siteLabel?: string | null
  trialEndsAt: Date | string | null
  stats: TrialExpiredStats
  onReactivate: () => void
  onLogout: () => void
  /**
   * Closes the paywall into the view-only app for this browser session. Omit it
   * and the paywall stays a wall (Log out is then the only way out).
   */
  onViewData?: () => void
  isRedirecting?: boolean
}

/**
 * Persistent paywall for an account whose 14-day trial ended without a
 * subscription (Paper "Home · Trial expired paywall"). Tracking is stopped,
 * collected data is saved, and there is one paid action: reactivate the plan
 * at its monthly price. "View my data" closes it into the view-only app (the
 * history reads, nothing new is created or shared); it comes back next session.
 */
export function TrialExpiredPaywall({
  open,
  planName,
  monthlyPrice,
  planSummary,
  siteLabel,
  trialEndsAt,
  stats,
  onReactivate,
  onLogout,
  onViewData,
  isRedirecting = false,
}: TrialExpiredPaywallProps) {
  const ended = formatShortDate(trialEndsAt)
  const subject = siteLabel ?? 'Your site'
  const title = ended
    ? `Your ${planName} trial ended. ${subject} stopped counting visits on ${ended}.`
    : `Your ${planName} trial ended. ${subject} stopped counting visits.`

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogPortal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-canvas/[0.78]" />
        <DialogPrimitive.Content
          onEscapeKeyDown={(e) => {
            e.preventDefault()
            onViewData?.()
          }}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          aria-describedby="trial-expired-desc"
          className="fixed inset-x-4 top-4 bottom-4 z-50 mx-auto flex max-w-[560px] flex-col overflow-y-auto rounded-2xl bg-card shadow-[0_0_0_1px_rgb(9_9_11/0.08),0_24px_48px_-12px_rgb(0_0_0/0.12)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08),0_24px_48px_-12px_rgb(0_0_0/0.6)] focus:outline-none sm:bottom-auto sm:top-[148px] sm:max-h-[calc(100svh-164px)]"
        >
          <div className="flex flex-col bg-urgency-ink-bg px-5 pb-6 pt-7 sm:px-7">
            <span className="inline-flex h-[22px] items-center gap-1.5 self-start rounded-full bg-urgency-rust-accent px-2 text-xs font-semibold leading-4 text-white">
              <ChipGlyph kind="pause" className="size-2.5" />
              Tracking paused
            </span>
            <DialogPrimitive.Title className="mt-4 text-[22px] font-semibold leading-7 tracking-[-0.02em] text-urgency-ink-fg">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description id="trial-expired-desc" className="mt-2 text-[13px] leading-[18px] text-white/60">
              Nothing you collected is lost. Subscribe and tracking starts again within a minute.
            </DialogPrimitive.Description>
          </div>

          <div className="flex flex-col gap-5 px-5 pt-6 sm:flex-row sm:px-7">
            <div className="flex min-w-0 flex-1 flex-col gap-2.5">
              <p className="text-xs font-medium leading-4 text-muted-foreground">Saved</p>
              {buildSavedList(stats).map((item) => (
                <div key={item} className="flex items-center gap-2 text-[13px] leading-[18px] text-foreground">
                  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden className="shrink-0 text-foreground">
                    <path d="m5 12.5 4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {item}
                </div>
              ))}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2.5">
              <p className="text-xs font-medium leading-4 text-muted-foreground">
                {ended ? `Stopped on ${ended}` : 'Stopped'}
              </p>
              {['Visits and pageviews', 'Clicks, forms and payments', 'Heatmap recording'].map((item) => (
                <div key={item} className="flex items-center gap-2 text-[13px] leading-[18px] text-foreground">
                  <ChipGlyph kind="pause" className="size-3.5 text-urgency-rust-accent" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 px-5 pb-7 pt-6 sm:px-7">
            <div className="flex items-start gap-3 rounded-xl px-[18px] py-4 shadow-[0_0_0_1.5px_var(--color-foreground)]">
              <span aria-hidden className="mt-px flex size-4 shrink-0 items-center justify-center rounded-full bg-foreground">
                <span className="size-1.5 rounded-full bg-background" />
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="text-sm font-semibold leading-[18px] text-foreground">{planName}</p>
                <p className="text-xs leading-4 text-muted-foreground">{planSummary}</p>
              </div>
              <p className="flex shrink-0 items-baseline gap-[3px]">
                <span className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground tabular-nums">
                  ${monthlyPrice}
                </span>
                <span className="text-xs leading-4 text-muted-foreground">/mo</span>
              </p>
            </div>
            <button
              type="button"
              onClick={onReactivate}
              disabled={isRedirecting}
              className={cn(
                'inline-flex h-11 items-center justify-center rounded-lg bg-urgency-ink-bg text-sm font-semibold text-white transition-colors hover:bg-urgency-ink-bg/90',
                'disabled:opacity-70',
              )}
            >
              {isRedirecting ? (
                <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin" />
              ) : (
                `Reactivate ${planName} · $${monthlyPrice}/mo`
              )}
            </button>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs leading-4 text-muted-foreground">Billed monthly through Stripe. Cancel anytime.</p>
              <div className="flex shrink-0 items-center gap-4">
                {onViewData && (
                  <button
                    type="button"
                    onClick={onViewData}
                    className="text-xs font-medium leading-4 text-foreground hover:text-muted-foreground"
                  >
                    View my data
                  </button>
                )}
                <button
                  type="button"
                  onClick={onLogout}
                  className="text-xs font-medium leading-4 text-foreground hover:text-muted-foreground"
                >
                  Log out
                </button>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
